import { ChargeItem, Encounter } from "@medplum/fhirtypes";

import {
  ENCOUNTER_CLASS_PLACE_OF_SERVICE,
  PlaceOfServiceValue,
} from "@/billing/constants/place-of-service";
import { CapturedPlaceOfService } from "@/billing/types/encounter-place-of-service";
import {
  replacePlaceOfServiceExtension,
  resolveChargeItemPlaceOfService,
} from "@/billing/utils/build-charge-item-payload";
import { Extensions } from "@/fhir/enums/extensions";

/**
 * The Place of Service an encounter's own location (`Encounter.class`) implies.
 *
 * Used as the fallback when none of the encounter's charges carry a Location yet, so the Billing tab
 * and newly captured charges start from where the visit happened.
 *
 * @param encounter - The encounter whose `class` coding is read.
 * @returns The matching `PLACE_OF_SERVICE_OPTIONS` value, or undefined when the encounter has no
 * class or one outside the encounter location value set.
 */
export function resolveEncounterPlaceOfService(
  encounter: Encounter | undefined,
): PlaceOfServiceValue | undefined {
  const system = encounter?.class?.system;
  const code = encounter?.class?.code;
  if (!system || !code) return undefined;

  // Own keys only, so a code such as `constructor` cannot match an inherited object property.
  if (!Object.hasOwn(ENCOUNTER_CLASS_PLACE_OF_SERVICE, system)) return undefined;
  const placeOfServiceByCode = ENCOUNTER_CLASS_PLACE_OF_SERVICE[system];
  return Object.hasOwn(placeOfServiceByCode, code) ? placeOfServiceByCode[code] : undefined;
}

/**
 * Whether a charge's Location was inferred from the visit location by charge capture.
 *
 * A charge with a Location and no marker holds one a biller owns. That includes every charge
 * captured before the marker existed, since capture then only ever copied a biller's value.
 *
 * @param chargeItem - The charge to read.
 * @returns True when the charge carries the inferred marker.
 */
export function isChargeItemPlaceOfServiceInferred(chargeItem: ChargeItem): boolean {
  return Boolean(
    chargeItem.extension?.some(
      (extension) =>
        extension.url === Extensions.ChargeItemPlaceOfServiceInferred &&
        extension.valueBoolean === true,
    ),
  );
}

/**
 * The Location a capture stamps on an encounter's new charges.
 *
 * A Location a biller owns on an active charge wins. Next comes the one the encounter's current
 * visit location implies, so an amended visit location reaches the charges. Last comes a value the
 * active charges inferred earlier, kept when the visit location no longer maps to one.
 *
 * @param activeChargeItems - The encounter's charges that are not voided.
 * @param encounter - The encounter whose `class` gives the visit location.
 * @returns The value and whether it was inferred, or undefined when nothing gives a Location.
 */
export function resolveCapturedPlaceOfService(
  activeChargeItems: ChargeItem[],
  encounter: Encounter,
): CapturedPlaceOfService | undefined {
  let previouslyInferred: string | undefined;

  for (const chargeItem of activeChargeItems) {
    const value = resolveChargeItemPlaceOfService(chargeItem);
    if (!value) continue;
    if (!isChargeItemPlaceOfServiceInferred(chargeItem)) return { value, isInferred: false };
    previouslyInferred ??= value;
  }

  const value = resolveEncounterPlaceOfService(encounter) ?? previouslyInferred;
  return value ? { value, isInferred: true } : undefined;
}

/**
 * The charges whose inferred Location no longer matches the one a capture resolved, rewritten to
 * it. Voided charges are included, so every charge on the encounter keeps the same Location.
 *
 * @param chargeItems - Every charge the encounter has.
 * @param placeOfService - The Location the capture resolved.
 * @returns The rewritten charges. Empty when the Location a biller owns wins, or nothing changed.
 */
export function refreshInferredPlaceOfService(
  chargeItems: ChargeItem[],
  placeOfService: CapturedPlaceOfService | undefined,
): ChargeItem[] {
  if (!placeOfService?.isInferred) return [];

  return chargeItems.reduce<ChargeItem[]>((refreshed, chargeItem) => {
    if (
      isChargeItemPlaceOfServiceInferred(chargeItem) &&
      resolveChargeItemPlaceOfService(chargeItem) !== placeOfService.value
    ) {
      refreshed.push({
        ...chargeItem,
        extension: replacePlaceOfServiceExtension(chargeItem.extension, placeOfService.value, true),
      });
    }
    return refreshed;
  }, []);
}
